<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.UI.8 — Usuarios y roles (canvas: Main › Usuarios y roles):
// JIT provisioning, role sync on every sign-in, the group mapping, and SCIM as
// "Próximamente".
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionSecuritySection from '../components/KaptionSecuritySection.vue';
import KaptionSecurityToggleRow from '../components/KaptionSecurityToggleRow.vue';
import KaptionSecurityProvisioningDialog from '../components/KaptionSecurityProvisioningDialog.vue';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const { overview, setOverview, settings } = useKaptionSecurity();
const { provider, domainsText, roleName } = useKaptionSecurityText(overview);

const mappingRef = ref(null);
const busy = ref(false);

const save = async changes => {
  busy.value = true;
  try {
    const { data } = await KaptionSecurityAPI.updateProvisioning(changes);
    setOverview(data);
    useAlert(t('KAPTION_SECURITY.COMMON.SAVED'));
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    busy.value = false;
  }
};

const jit = computed({
  get: () => !!settings.value.jit_enabled,
  set: value => save({ jit_enabled: value }),
});
const roleSync = computed({
  get: () => !!settings.value.update_role_on_sign_in,
  set: value => save({ update_role_on_sign_in: value }),
});

const mappings = computed(() => settings.value.group_mappings || []);
const fallback = computed(() =>
  settings.value.unmapped_action === 'deny'
    ? t('KAPTION_SECURITY.USERS.UNMAPPED_DENY')
    : t('KAPTION_SECURITY.USERS.UNMAPPED_AGENT')
);
</script>

<template>
  <div class="flex flex-col gap-11">
    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.USERS.TITLE')"
      :description="t('KAPTION_SECURITY.USERS.DESC', { provider })"
    >
      <div class="border rounded-xl border-n-weak">
        <KaptionSecurityToggleRow
          v-model="jit"
          :title="t('KAPTION_SECURITY.USERS.JIT_TITLE')"
          :description="
            t('KAPTION_SECURITY.USERS.JIT_DESC', {
              domains: domainsText,
              provider,
            })
          "
          :disabled="busy"
          data-testid="ks-toggle-jit"
        />
        <KaptionSecurityToggleRow
          v-model="roleSync"
          class="border-t border-n-weak"
          :title="t('KAPTION_SECURITY.USERS.ROLE_SYNC_TITLE')"
          :description="
            t('KAPTION_SECURITY.USERS.ROLE_SYNC_DESC', { provider })
          "
          :disabled="busy"
          data-testid="ks-toggle-role-sync"
        />
        <KaptionSecurityToggleRow
          :model-value="false"
          class="border-t border-n-weak"
          :title="t('KAPTION_SECURITY.USERS.SCIM_TITLE', { provider })"
          :description="t('KAPTION_SECURITY.USERS.SCIM_DESC')"
          soon
        />
      </div>
    </KaptionSecuritySection>

    <KaptionSecuritySection :title="t('KAPTION_SECURITY.USERS.MAPPINGS_TITLE')">
      <template #actions>
        <Button
          sm
          faded
          type="button"
          :label="t('KAPTION_SECURITY.USERS.EDIT_MAPPING')"
          data-testid="ks-edit-mapping"
          @click="mappingRef.open()"
        />
      </template>
      <p v-if="!mappings.length" class="m-0 text-sm text-n-slate-11">
        {{ t('KAPTION_SECURITY.USERS.MAPPINGS_EMPTY', { fallback }) }}
      </p>
      <div v-else class="border rounded-xl border-n-weak">
        <div
          v-for="(row, index) in mappings"
          :key="row.group"
          class="flex items-center gap-3 px-4 py-3 text-sm"
          :class="{ 'border-t border-n-weak': index > 0 }"
        >
          <code
            class="px-2 py-0.5 text-[13px] rounded-md bg-n-alpha-2 text-n-slate-12"
            >{{ row.group }}</code
          >
          <span
            class="i-lucide-arrow-right size-3.5 text-n-slate-10"
            aria-hidden="true"
          />
          <span class="flex-1 text-n-slate-12">{{ roleName(row.role) }}</span>
          <span class="text-[13px] text-n-slate-11">{{
            row.team_ids?.length
              ? `${row.team_ids.length} ${t('KAPTION_SECURITY.USERS.HEAD_TEAMS').toLowerCase()}`
              : t('KAPTION_SECURITY.USERS.NO_TEAMS')
          }}</span>
        </div>
        <div
          class="px-4 py-3 text-[13px] border-t border-n-weak text-n-slate-11"
        >
          {{ t('KAPTION_SECURITY.USERS.UNMAPPED_LABEL') }}: {{ fallback }}
        </div>
      </div>
    </KaptionSecuritySection>

    <KaptionSecurityProvisioningDialog ref="mappingRef" />
  </div>
</template>
